import firebase from "firebase/app";

import { getUserId } from "src/config/storage";
import firestore from "src/apiService/firestore";
import auth from "src/apiService/auth";
import { deleteUploadedFile, uploadFile } from "src/utils/storage";
import type { ThemePreferences } from "src/interfaces/systemState.interface";
import type { Attribution } from "src/utils/attribution";
import { metaBrowserContext } from "src/utils/meta";

export const fetchLoginApi = async (email: string, password: string) => {
  const userCredential: any = await auth().signInWithEmailAndPassword(
    email,
    password
  );
  const user: firebase.User = userCredential.user;
  const token: string = await user.getIdToken();
  const claims = (await user.getIdTokenResult()).claims;
  return { token, uid: user.uid, claims };
};

export const signupApi = async (
  email: string,
  password: string,
  attribution?: Attribution | null
) => {
  const signUpFn = firebase.functions().httpsCallable("signUp");
  await signUpFn({
    email,
    password,
    attribution: attribution || undefined,
    meta: metaBrowserContext(),
  });
  return fetchLoginApi(email, password);
};

export const loginWithTokenApi = async (token: string) => {
  const userCredential: any = await auth().signInWithCustomToken(token);
  const user: firebase.User = userCredential.user;
  const claims = (await user.getIdTokenResult()).claims;
  return { token, uid: user.uid, claims };
};

export const logoutApi = () => auth().signOut();

export const resetPasswordApi = (email: string) => {
  return auth().sendPasswordResetEmail(email);
};

export async function updateUserApi(payload: any) {
  const db = firestore();
  const userId = await getUserId();
  const doc = db.collection("Users").doc(userId);

  // Taken from POST `/user`:
  const data: any = {};

  if (payload.income_tax_rate) data.income_tax_rate = payload.income_tax_rate;

  if (payload.customer_id) data.customer_id = payload.customer_id;

  if (payload.pm_id) data.pm_id = payload.pm_id;

  if ("salesQuickView" in payload) data.salesQuickView = payload.salesQuickView;

  if ("getStartedStep" in payload) data.getStartedStep = payload.getStartedStep;

  if ("inventoryType" in payload) data.inventoryType = payload.inventoryType;

  if ("checklist_completed" in payload)
    data.checklist_completed = payload.checklist_completed;

  if ("checklist_items" in payload)
    data.checklist_items = payload.checklist_items;

  if ("getStartedStart" in payload)
    data.getStartedStart = payload.getStartedStart;

  if ("getStartedEnd" in payload) data.getStartedEnd = payload.getStartedEnd;

  if ("dontShowDialogs" in payload)
    data.dontShowDialogs = payload.dontShowDialogs;

  if (payload.cancellation) data.cancellation = payload.cancellation;

  if ("dashboardDateRange" in payload) {
    if (payload.dashboardDateRange === undefined) {
      data.dashboardDateRange = firebase.firestore.FieldValue.delete();
    } else {
      data.dashboardDateRange = payload.dashboardDateRange;
    }
  }
  if ("transactionDateRange" in payload) {
    if (payload.transactionDateRange === undefined) {
      data.transactionDateRange = firebase.firestore.FieldValue.delete();
    } else {
      data.transactionDateRange = payload.transactionDateRange;
    }
  }

  if ("timezone" in payload) {
    if (payload.timezone === undefined) {
      data.timezone = firebase.firestore.FieldValue.delete();
    } else {
      data.timezone = payload.timezone;
    }
  }

  return await doc.update(data);
}

export const fetchStripeLink = async () => {
  const f = firebase.functions().httpsCallable("stripeGetPortal");
  const { data } = await f();
  return data;
};

export async function deleteAccountData() {
  const f = firebase.functions().httpsCallable("deleteAccountData");
  return await f();
}

export async function updateCompanyInformation(update: {
  name?: string;
  logo?: any;
}) {
  const db = firestore();
  const user = await getUserId();
  const logoPath = `${user}/company-logo.png`;
  const changes = { ...update };
  if ("logo" in update && update.logo) changes.logo = logoPath;

  const oldLogo = await db.runTransaction(async (t) => {
    const ref = db.collection("Users").doc(user);
    const doc = await t.get(ref);
    const data = doc.data();
    if (!doc.exists || !data) throw new Error(`User ${user} doesnt exist`);

    const company = {
      ...data.company,
      ...changes,
    };

    for (const [k, v] of Object.entries(company)) {
      if (v === undefined) delete company[k];
    }

    await t.update(ref, {
      company,
    });

    return data.company?.logo;
  });

  if (update?.logo) {
    await uploadFile(logoPath, update.logo);
  } else if (oldLogo && "logo" in update && !update.logo) {
    await deleteUploadedFile(oldLogo);
  }
}

export async function updateThemePreferencesApi(
  preferences: Partial<ThemePreferences>
) {
  const db = firestore();
  const userId = await getUserId();

  return await db.runTransaction(async (t) => {
    const ref = db.collection("Users").doc(userId);
    const doc = await t.get(ref);

    if (!doc.exists) {
      throw new Error(`User ${userId} doesn't exist`);
    }

    const userData = doc.data();
    const existingPreferences = userData?.themePreferences || {};

    const mergedPreferences: any = {
      ...existingPreferences,
      ...preferences,
    };

    if (preferences.customLightPalette) {
      mergedPreferences.customLightPalette = {
        ...(existingPreferences.customLightPalette || {}),
        ...preferences.customLightPalette,
      };
    }

    if (preferences.customDarkPalette) {
      mergedPreferences.customDarkPalette = {
        ...(existingPreferences.customDarkPalette || {}),
        ...preferences.customDarkPalette,
      };
    }

    await t.update(ref, {
      themePreferences: mergedPreferences,
    });
  });
}

export async function resetThemePreferencesApi() {
  const db = firestore();
  const userId = await getUserId();
  const doc = db.collection("Users").doc(userId);

  return await doc.update({
    themePreferences: firebase.firestore.FieldValue.delete(),
  });
}
